import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUsersStore } from '../../store/useUsersStore';
import { useNavigate } from 'react-router-dom';
import {
  FREELANCER_UI_GROUPS,
  FREELANCER_BUDGETS,
  getPresetValue,
  getRangeForCurrency,
} from '../../utils/freelancerBudgets';
import {
  FREELANCER_HOURLY_BUDGETS,
  getHourlyPresetValue,
  getHourlyRange,
} from '../../utils/freelancerHourlyBudgets';

const ACCENT = '#1F4ECF';
const TIER_ORDER = ['task','micro','entry','core','small','medium','business','pro','large'];
const HOURLY_ORDER = ['basic','moderate','standard','skilled'];

const makeDefaultSettings = () => {
  const fixed = {};
  for (const tier of TIER_ORDER) {
    fixed[tier] = {};
    for (const c of (Object.keys(FREELANCER_BUDGETS[tier] || {}))) fixed[tier][c] = null;
  }
  const hourly = {};
  for (const tier of HOURLY_ORDER) {
    hourly[tier] = {};
    for (const c of (Object.keys(FREELANCER_HOURLY_BUDGETS[tier] || {}))) hourly[tier][c] = null;
  }
  return { fixed, hourly };
};

const BidPriceFilter = ({ subUserId: propSubUserId = null }) => {
  const users = useUsersStore((s) => s.users);
  const selectedKey = useUsersStore((s) => s.selectedKey);
  const updateSubUser = useUsersStore((s) => s.updateSubUser);
  const navigate = useNavigate();

  const resolvedSubUserId = useMemo(() => {
    if (propSubUserId) return String(propSubUserId);
    if (!selectedKey || !Array.isArray(users)) return null;
    const found = users.find((u) => {
      const ids = [u.sub_user_id, u.document_id, u.id, u._id, u.uid, u.sub_username];
      return ids.some((id) => id && String(id) === String(selectedKey));
    });
    return found ? String(found.sub_user_id || found.document_id || found.id || found._id || found.uid) : null;
  }, [propSubUserId, selectedKey, users]);

  const currentUser = useMemo(() => {
    if (!resolvedSubUserId || !Array.isArray(users)) return null;
    return users.find((u) => {
      const ids = [u.sub_user_id, u.document_id, u.id, u._id, u.uid, u.sub_username];
      return ids.some((id) => id && String(id) === String(resolvedSubUserId));
    }) || null;
  }, [resolvedSubUserId, users]);

  const initialSettings = useMemo(() => {
    const existing = currentUser?.bid_price || currentUser?.bid_price_settings || {};
    const defaults = makeDefaultSettings();
    const fixed = { ...defaults.fixed };
    const hourly = { ...defaults.hourly };

    for (const tier of Object.keys(fixed)) {
      fixed[tier] = { ...(fixed[tier] || {}), ...(existing.fixed?.[tier] || {}) };
    }
    for (const tier of Object.keys(hourly)) {
      hourly[tier] = { ...(hourly[tier] || {}), ...(existing.hourly?.[tier] || {}) };
    }

    return { fixed, hourly };
  }, [currentUser]);

  const snapshotJson = useMemo(() => JSON.stringify(initialSettings), [initialSettings]);
  const [settings, setSettings] = useState(initialSettings);
  const prevSnapshotRef = useRef(snapshotJson);

  const [activeTab, setActiveTab] = useState('fixed');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (prevSnapshotRef.current !== snapshotJson) {
      setSettings(initialSettings);
      prevSnapshotRef.current = snapshotJson;
    }
    setMsg(null);
    setErr(null);
  }, [snapshotJson, initialSettings, resolvedSubUserId]);

  const setFixedCurrencyValue = useCallback((tier, currency, value) => {
    setSettings((s) => {
      const next = { ...s, fixed: { ...(s.fixed || {}) } };
      next.fixed[tier] = { ...(next.fixed[tier] || {}) };
      next.fixed[tier][currency] = value === '' ? null : Number(value);
      return next;
    });
  }, []);

  const setHourlyCurrencyValue = useCallback((tier, currency, value) => {
    setSettings((s) => {
      const next = { ...s, hourly: { ...(s.hourly || {}) } };
      next.hourly[tier] = { ...(next.hourly[tier] || {}) };
      next.hourly[tier][currency] = value === '' ? null : Number(value);
      return next;
    });
  }, []);

  const applyPresetToTier = useCallback((tier, preset) => {
    setSettings((s) => {
      const next = { ...s, fixed: { ...(s.fixed || {}) } };
      next.fixed[tier] = { ...(next.fixed[tier] || {}) };
      const currencies = Object.keys(FREELANCER_BUDGETS[tier] || {});
      for (const c of currencies) {
        const rng = getRangeForCurrency(tier, c);
        next.fixed[tier][c] = rng ? getPresetValue(rng[0], rng[1], preset) : null;
      }
      return next;
    });
  }, []);

  const applyHourlyPresetToTier = useCallback((tier, preset) => {
    setSettings((s) => {
      const next = { ...s, hourly: { ...(s.hourly || {}) } };
      next.hourly[tier] = { ...(next.hourly[tier] || {}) };
      const currencies = Object.keys(FREELANCER_HOURLY_BUDGETS[tier] || {});
      for (const c of currencies) {
        const rng = getHourlyRange(tier, c);
        if (!rng) { next.hourly[tier][c] = null; continue; }
        const [min, max] = rng;
        next.hourly[tier][c] = getHourlyPresetValue(min, max, preset, tier);
      }
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    setErr(null);
    setMsg(null);
    if (!resolvedSubUserId) {
      setErr('No sub-user selected.');
      return;
    }

    const payload = {
      bid_price_settings: {
        ...(currentUser?.bid_price_settings || {}),
        fixed: settings.fixed || {},
        hourly: settings.hourly || {},
      },
    };

    setSaving(true);
    try {
      await updateSubUser(resolvedSubUserId, payload);
      setMsg('Saved successfully.');
    } catch (e) {
      console.error('Failed to save bid price settings', e);
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [resolvedSubUserId, settings, updateSubUser, currentUser]);

  const resetAllFields = useCallback(() => {
    setSettings(makeDefaultSettings());
    setMsg(null);
    setErr(null);
  }, []);

  const renderTierCard = (tier) => {
    const values = (settings.fixed && settings.fixed[tier]) || {};
    const currencies = Object.keys(FREELANCER_BUDGETS[tier] || {});
    return (
      <div key={tier} className="bg-white border border-gray-100 rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold px-2 py-1 rounded bg-green-50 text-green-700">Fixed</span>
              <h3 className="text-lg font-semibold capitalize text-gray-900">{tier.replace('_',' ')}</h3>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {['lowest','average','highest'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => applyPresetToTier(tier, p)}
                className="flex items-center justify-center text-sm font-medium text-white hover:opacity-90"
                style={{
                  width: 105, 
                  height: 40,
                  backgroundColor: ACCENT,
                  borderRadius: 5,
                  cursor: 'pointer'
                }}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {currencies.map((c) => {
            const rng = getRangeForCurrency(tier, c);
            const hint = rng ? `${rng[0]}–${rng[1]} ${c}` : `${c}`;
            return (
              <div key={c} className="space-y-2">
                <label className="text-xs text-gray-600 font-medium">{c} <span className="text-xs text-gray-400">({hint})</span></label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="i.e. 20"
                  value={values[c] == null ? '' : values[c]}
                  onChange={(e) => setFixedCurrencyValue(tier, c, e.target.value)}
                  style={{
                    width: '100%',
                    height: 50,
                    borderRadius: 10,
                    padding: '12px 14px',
                    outline: 'none',
                    boxShadow: 'none',
                    boxSizing: 'border-box'
                  }}
                  className="placeholder-gray-400 text-sm border-[1.5px]  border-primary-blue  focus:border-2 bg-white"
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderHourlyCard = (tier) => {
    const values = (settings.hourly && settings.hourly[tier]) || {};
    const currencies = Object.keys(FREELANCER_HOURLY_BUDGETS[tier] || {});
    return (
      <div key={tier} className="bg-white border border-gray-100 rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold px-2 py-1 rounded bg-indigo-50 text-indigo-700">Hourly</span>
              <h3 className="text-lg font-semibold capitalize text-gray-900">{tier}</h3>
            </div>
            <p className="text-xs text-gray-500 mt-2">Set hourly rate per currency for this tier</p>
          </div>

          <div className="flex items-center gap-3">
            {['lowest','average','highest'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => applyHourlyPresetToTier(tier, p)}
                className="flex items-center justify-center text-sm font-medium text-white hover:opacity-90"
                style={{
                  width: 105,
                  height: 40,
                  backgroundColor: ACCENT,
                  borderRadius: 5,
                  cursor: 'pointer'
                }}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {currencies.map((c) => {
            const rng = getHourlyRange(tier, c);
            const hint = rng ? `${rng[0]}–${rng[1]} ${c} / hr` : `${c}`;
            return (
              <div key={c} className="space-y-2">
                <label className="text-xs text-gray-600 font-medium">{c} <span className="text-xs text-gray-400">({hint})</span></label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Hourly rate"
                  value={values[c] == null ? '' : values[c]}
                  onChange={(e) => setHourlyCurrencyValue(tier, c, e.target.value)}
                  style={{
                    width: '100%',
                    height: 50,
                    borderRadius: 10,
                    padding: '12px 14px',
                    outline: 'none',
                    boxShadow: 'none',
                    boxSizing: 'border-box'
                  }}
                  className="placeholder-gray-400 text-sm border-[1.5px]  border-primary-blue  focus:border-2  bg-white"
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full bg-gray-50 py-10 px-6 ">
      <div className=" mx-auto  w-full ">
        <header className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate('/dashboard/more-settings')} className="flex items-center justify-center p-2">
              <img src="/Icons/arrow-back.svg" alt="Back" className="w-8 h-8" />
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Bid Price</h1>
          </div>

          <div className="mb-6">
            <div className="p-4 rounded-lg bg-white border border-gray-200 text-sm text-gray-700">
              You don't have to fill in all the prices. Just enter amounts for the ranges where you want to bid a specific price.
            </div>
          </div>

          {/* Tabs centered - wide (matches figma), radius 5 */}
          <div className="mb-8 flex justify-center">
            <div className="flex gap-2 mb-6 bg-white p-2 rounded-xl shadow-sm w-full border-[1.5px] border-light-blue">
              <button
                type="button"
                onClick={() => setActiveTab('fixed')}
                style={{
                  height: 45,
                  borderRadius: 5,
                  backgroundColor: activeTab === 'fixed' ? ACCENT : '',
                  color: activeTab === 'fixed' ? '#ffffff' : ACCENT,
                  width: '50%'
                }}
                className="font-medium text-base bg-light-blue-shade-20 hover:opacity-90 cursor-pointer"
              >
                Fixed
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('hourly')}
                style={{
                  height: 45,
                  borderRadius: 5,
                  backgroundColor: activeTab === 'hourly' ? ACCENT : '',
                  color: activeTab === 'hourly' ? '#ffffff' : ACCENT,
                  width: '50%'
                }}
                className="font-medium text-base bg-light-blue-shade-20 hover:opacity-90 cursor-pointer"
              >
                Hourly
              </button>
            </div>
          </div>
        </header>

        <section className="space-y-6">
          {activeTab === 'fixed' ? (
            <div className="space-y-6">{TIER_ORDER.map((tier) => renderTierCard(tier))}</div>
          ) : (
            <div className="space-y-6">{HOURLY_ORDER.map((tier) => renderHourlyCard(tier))}</div>
          )}
        </section>

        <div className="mt-6 flex items-center justify-end gap-3">
          <div className="text-sm text-gray-600 mr-auto">{msg ? <span className="text-green-600">{msg}</span> : (err || '')}</div>
          <button
            type="button"
            onClick={resetAllFields}
            className="px-4 py-2 border-[1.5px] rounded text-sm hover:bg-gray-100"
            style={{ cursor: 'pointer', borderRadius: 5 }}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !resolvedSubUserId}
            className="text-white text-sm hover:opacity-95"
            style={{
              width: 105,
              height: 40,
              backgroundColor: ACCENT,
              borderRadius: 5,
              cursor: saving || !resolvedSubUserId ? 'default' : 'pointer'
            }}
          >
            {saving ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="absolute inset-0 bg-black opacity-40" onClick={() => setShowModal(false)} />
          <div className="bg-white p-6 rounded shadow-lg z-10 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-2">How Bid Price Presets Work</h3>
            <p className="text-sm text-gray-700">Presets apply per-currency ranges from the data files. Lowest = min, Average = midpoint (Expert uses special rule), Highest = max.</p>
            <div className="mt-4 flex justify-end"><button type="button" onClick={() => setShowModal(false)} className="px-3 py-1 border rounded">Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(BidPriceFilter);